<template>
    <div>
        <div class="card shadow">
            <div class="card-header bg-transparent">
                <div>
                    <h3 class="mb-0">
                        {{ $tc('message.antecedents', 2) }}
                    </h3>
                </div>
            </div>

            <div class="card-body">
                <div class="card-title">
                    <router-link :to="{ name: 'add-antecedent' }" v-if="can">
                        {{ $t('message.add') }}

                        <i class="fas fa-plus-circle edit-icon"></i>
                    </router-link>
                </div>

                <template v-if="rows">
                    <b-container>
                        <b-row class="justify-content-sm-center">
                            <b-col col sm="6">
                                <b-form-group>
                                    <autocomplete
                                            ref="autocomplete"
                                            :placeholder="$t('message.search')"
                                            :source="mAntecedents"
                                            input-class="form-control"
                                            results-property="data"
                                            :results-display="formattedDisplay"
                                            @selected="itemSelected"
                                    ></autocomplete>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-container>

                    <b-table
                            id="antecedents-table"
                            primary-key="id"
                            :per-page="perPage"
                            :current-page="currentPage"
                            selectable
                            select-mode="single"
                            borderless
                            responsive
                            striped
                            hover
                            :sort-by.sync="sortBy"
                            :sort-desc.sync="sortDesc"
                            @row-selected="rowSelected"
                            :fields="fields"
                            :items="mAntecedents"
                    >
                        <!-- A virtual composite column for actions -->
                        <template slot="action" slot-scope="data">
                            <div class="row" v-if="data.item.isAuthor || hasMedecinControleRole || hasAdminRole || hasAssistante">
                                <div class="col-sm-6">
                                    <b-link :to="{ name: 'edit-antecedent', params: { antecedent: data.item.slug } }">
                                        <i class="fas fa-edit fa-2x edit-icon"></i>
                                    </b-link>
                                </div>

                                <div class="col-sm-6">
                                    <i v-b-modal="'delete-modal-' + data.item.slug" class="fas fa-trash fa-2x delete-icon"></i>
                                </div>
                            </div>

                            <b-modal
                                    :id="'delete-modal-' + data.item.slug"
                                    size="sm"
                                    header-bg-variant="danger"
                                    header-text-variant="light"
                                    :title="$t('message.delete') + ' ' + $tc('message.antecedents', 1)"
                                    hide-footer
                            >
                                <div class="d-block text-center">
                                    <h3>{{ $t('message.delete') }} {{ data.item.type }}?</h3>
                                </div>

                                <div class="d-block float-right">
                                    <b-button class="secondary" @click="deleteAntecedent(data.item.slug)">
                                        {{ $t('message.delete') }}
                                    </b-button>

                                    <b-button class="danger" @click="$bvModal.hide('delete-modal-' + data.item.slug)">
                                        {{ $t('message.cancel') }}
                                    </b-button>
                                </div>
                            </b-modal>
                        </template>
                    </b-table>

                    <b-pagination
                            class="justify-content-center"
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="perPage"
                            aria-controls="antecedents-table"
                    ></b-pagination>
                </template>

                <template v-else>
                    <div class="card">
                        <div class="card-body text-xs-center">
                            <p class="card-text">
                                {{ $t('message.noResults') }}
                            </p>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import moment from 'moment';
    import _ from 'lodash';

    export default {
        name: "Antecedents",

        computed: {
            ...mapGetters(["antecedents", "hasAdminRole", "hasMedecinControleRole", "hasPraticienRole", 'hasAssistante']),

            can: function (){
                if (this.hasAdminRole){
                    return true;
                }else{
                    return this.hasMedecinControleRole || this.hasPraticienRole || this.hasAssistante;
                }
            },
            mAntecedents: function() {
                let aArray = [];

                _.forEach(this.antecedents, function(antecedent) {
                    let a = {};
                    if (typeof antecedent.user == 'object'){
                        let user = antecedent.user;
                        let fullName =  user.prenom? user.prenom  : '' + (user.nom).toUpperCase();
                        a.patient = fullName+ " ( "+ antecedent.dossier.numero_dossier + " ) ";
                    }
                    a.type = antecedent.type;
                    a.description = antecedent.description.length > 20 ? antecedent.description.substr(0, 20) + '...' : antecedent.description;
                    a.date = moment(antecedent.date).format('YYYY/MM/DD');
                    a.id = antecedent.id;
                    a.slug = antecedent.slug;
                    a.isAuthor = antecedent.isAuthor;

                    aArray.push(a);
                });

                return aArray;
            },

            rows: function() {
                return this.mAntecedents.length;
            }
        },

        data() {
            return {
                perPage: 10,
                currentPage: 1,
                sortBy: 'date',
                sortDesc: true,
                fields: [
                    {
                        key: 'patient',
                        label: this.$i18n.tc('message.patients',2),
                    },
                    {
                        key: 'type',
                        label: this.$i18n.tc('message.types', 1),
                    },
                    {
                        key: 'description',
                        label: this.$i18n.t('message.description'),
                    },
                    {
                        key: 'date',
                        label: this.$i18n.t('message.startDate'),
                        sortable: true
                    },
                    {
                        key: 'action',
                        label: this.$i18n.tc('message.operations', 1)
                    }
                ],
                offset: 0,
                limit: -1,
            }
        },

        methods: {
            getAntecedents() {
                let args = {
                    offset: this.offset,
                    limit: this.limit
                };

                this.$store.dispatch('getAntecedents', { args : args });
            },

            formattedDisplay (result) {
                return result.title;
            },

            rowSelected(items) {
                this.$router.push({ name: 'antecedent', params: { antecedent: items[0].slug } });
            },

            itemSelected(item) {
                this.$router.push({ name: 'antecedent', params: { antecedent: item.selectedObject.slug } });
            },

            deleteAntecedent(antecedent) {
                let self = this;

                let args = {
                    antecedent: antecedent
                };

                let successMessage = this.$i18n.t('message.antecedentDeleted');
                let errorMessages = {
                    generic: this.$i18n.t('message.genericError'),
                    error: this.$i18n.t('message.error')
                };

                this.$store.dispatch('deleteAntecedent', { args, index: antecedent, successMessage, errorMessages })
                    .then(() => {
                        self.$bvModal.hide('delete-modal-' + antecedent);
                    });
            }
        },

        mounted() {
            // Fetch antecedent list
            this.getAntecedents();
        }
    }
</script>

<style scoped>

</style>